// src/hooks/useAnalytics.ts
import { useCallback } from "react";
// eslint-disable-next-line import/no-extraneous-dependencies
import * as amplitude from "@amplitude/analytics-browser";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import * as userSelectors from "redux/user/selectors";
import { STORAGE_LANGUAGE_NAME } from "constants/variables";

/** Возвращает тип устройства: Mobile или Desktop */
const getDeviceType = (): string =>
  /Mobi|Android/i.test(navigator.userAgent) ? "Mobile" : "Desktop";

/** Возвращает ОС пользователя */
const getOS = (): string => {
  const ua = navigator.userAgent;
  if (/Windows NT/i.test(ua)) {
    return "Windows";
  }
  if (/Mac OS X/i.test(ua)) {
    return "macOS";
  }
  if (/Android/i.test(ua)) {
    return "Android";
  }
  if (/iPhone|iPad|iPod/i.test(ua)) {
    return "iOS";
  }
  if (/Linux/i.test(ua)) {
    return "Linux";
  }
  return "Unknown";
};

/** Извлекает код страны из настроек браузера */
const getCountry = (): string | undefined => {
  const locale = navigator.language ?? navigator.languages?.[0];
  return locale?.split("-")[1];
};

/** Возвращает язык приложения из localStorage */
const getLocale = (): string | null => {
  return localStorage.getItem(STORAGE_LANGUAGE_NAME);
};

/** Возвращает текущую метку времени в формате ISO 8601 */
export const getTimeStamp = (): string => {
  return new Date().toISOString();
};

/** Хук для трекинга событий через Amplitude */
export const useAnalytics = () => {
  // хуки на верхнем уровне
  const userId = useSelector(userSelectors.id);
  const location = useLocation();

  /** Функция трекинга событий */
  const trackEvent = useCallback(
    (
      eventType: string,
      eventProperties: Record<string, any> = {},
      oncePerSession: boolean = false,
    ) => {
      if (oncePerSession) {
        const key = `amplitude_${eventType}`;
        if (sessionStorage.getItem(key)) {
          return;
        }
        sessionStorage.setItem(key, "true");
      }

      amplitude.track({
        // eslint-disable-next-line camelcase
        event_type: eventType,
        // eslint-disable-next-line camelcase
        event_properties: {
          userId,
          ...eventProperties,
        },
      });
    },
    [userId],
  );

  /** Текущий путь из React Router */
  const getLocationPathname = (): string => {
    return location.pathname;
  };

  /** Извлекает campaign_id из URL */
  const getCampainId = (): string | undefined => {
    return (
      new URLSearchParams(window.location.search).get("campaign_id") ??
      undefined
    );
  };

  return {
    trackEvent,
    getDeviceType,
    getOS,
    getCountry,
    getLocale,
    getTimeStamp,
    getLocationPathname,
    getCampainId,
  };
};
